import { TAU } from "../core/math";
import type { Vec2 } from "../core/vec2";
import { outlinePolygon } from "../physics/shapes";
import type { ShapeDef } from "../physics/shapes";
import type { Body } from "../physics/world";
import { GIZMO, type SymmetryHandle, type SymmetryState } from "../symmetry/symmetry";
import type { Camera } from "./camera";
import type { Layer } from "./layer";

const ACCENT = "#4f8cff";
const ACCENT_SOFT = "rgba(79, 140, 255, 0.22)";
const HOT = "#ffb347";
const INK = "rgba(18, 20, 26, 0.55)";

export interface OverlayState {
  symmetry: SymmetryState;
  hoverHandle: SymmetryHandle;
  activeHandle: SymmetryHandle;
  /** Cursor del pincel en pantalla (null = puntero fuera). */
  cursor: Vec2 | null;
  /** Radio del pincel en px de pantalla. */
  cursorRadius: number;
  /** Cuerda del estabilizador en pantalla (null = sin arrastre). */
  stabilizeString: { anchor: Vec2; tip: Vec2; cursor: Vec2; slack: number; tension: number; curve: number; drop: number; swing: number } | null;
  /** Forma en previsualizacion bajo el cursor (herramienta forma). */
  previewShape: ShapeDef | null;
  /** Pivote de transformacion de la seleccion, en mundo. */
  transformPivot?: Vec2 | null;
  /** Cuerpo resaltado (hover o arrastre). */
  highlight: Body | null;
  /** Muestra la caja de contencion de la fisica. */
  showWalls: boolean;
  walls: { x: number; y: number; w: number; h: number };
  /** Dibuja los colisionadores encima del campo (depuracion). */
  debugColliders: boolean;
  /** Dibuja el radio de alcance de puentes de cada cuerpo. */
  showBridgeReach: boolean;
  /** Alcance global de puentes (para cuerpos que usan el valor global). */
  bridgeReach: number;
  bodies: readonly Body[];
  /** Selección universal (flecha). */
  selectBounds?: import("../scene/types").Rect | null;
  selectPivot?: Vec2 | null;
  selectOp?: import("../app/editor").SelectOp;
  isSelectTool?: boolean;
}

/**
 * Capa de guias: gizmo de simetria, cursor del pincel, previsualizacion de
 * forma y ayudas de depuracion. Nunca se exporta ni entra en el historial.
 */
export class OverlayRenderer {
  render(layer: Layer, state: OverlayState, camera: Camera): void {
    const ctx = layer.ctx;
    ctx.setTransform(layer.dpr, 0, 0, layer.dpr, 0, 0);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    if (state.showWalls) this.drawWalls(ctx, state.walls, camera);
    if (state.debugColliders) this.drawColliders(ctx, state.bodies, camera);
    if (state.showBridgeReach) this.drawBridgeReach(ctx, state.bodies, state.bridgeReach, camera);
    // Select tool uses its own box+pivot; don't double-draw the generic transform pivot then.
    if (state.isSelectTool && state.selectBounds) this.drawSelectBox(ctx, state.selectBounds, state.selectPivot ?? null, state.selectOp ?? "move", camera);
    else if (state.transformPivot) this.drawPivot(ctx, state.transformPivot, camera);
    if (state.highlight) this.drawHighlight(ctx, state.highlight, camera);
    else if (state.selectBounds && state.selectOp !== undefined) {
      // Even when not on select tool, show faint box if something selected (optional, muted)
    }
    if (state.previewShape && state.cursor) {
      this.drawShapePreview(ctx, state.previewShape, state.cursor, camera);
    }
    if (state.symmetry.visible && state.symmetry.mode !== "none") {
      this.drawSymmetry(ctx, state, camera);
    } else if (state.symmetry.visible && state.activeHandle) {
      this.drawSymmetry(ctx, state, camera);
    }
    if (state.stabilizeString) {
      this.drawStabilizeString(ctx, state.stabilizeString);
    }
    if (state.cursor && !state.previewShape) {
      this.drawCursor(ctx, state.cursor, state.cursorRadius);
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  private drawCursor(ctx: CanvasRenderingContext2D, p: Vec2, radius: number): void {
    const r = Math.max(1.5, radius);
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, TAU);
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Control de la cuerda: cuando Balanceo=0 es EXACTO al HTML (U estática inmediata).
  // Con Balanceo>0 el punto de control persigue al ideal con muelle.
  private _currentCX = 0; private _currentCY = 0;
  private _velX = 0; private _velY = 0;
  private _hasControl = false;
  private _prevT = 0;

  private drawStabilizeString(ctx: CanvasRenderingContext2D, state: NonNullable<OverlayState["stabilizeString"]>): void {
    const { anchor, tip, slack, curve: curveRaw, drop: dropRaw, swing: swingRaw } = state as unknown as { anchor: Vec2; tip: Vec2; slack: number; curve: number; drop: number; swing: number };
    const ropeLength = slack ?? Math.hypot(tip.x - anchor.x, tip.y - anchor.y);
    const curve = curveRaw ?? 0.65;
    const drop = dropRaw ?? 0.14;
    const swingAmt = swingRaw ?? 0;
    const gravityFactor = curve + drop * 0.35;
    const dx = anchor.x - tip.x, dy = anchor.y - tip.y;
    const distanciaActual = Math.hypot(dx, dy);
    if (ropeLength < 6) return;
    const holgura = Math.max(0, ropeLength - distanciaActual);
    const tensa = distanciaActual >= ropeLength - 0.5;

    // Posición ideal del HTML: mid + holgura * gravityFactor (tu simulación exacta)
    const midX = (tip.x + anchor.x) / 2;
    const midY = (tip.y + anchor.y) / 2;
    const targetCX = midX;
    const targetCY = midY + holgura * gravityFactor;

    let useCX: number, useCY: number;
    if (swingAmt < 0.01) {
      // HTML PURO: sin inercia, U inmediata. Resetea velocidad para no arrastrar rebote previo.
      useCX = targetCX; useCY = targetCY;
      this._currentCX = targetCX; this._currentCY = targetCY;
      this._velX = 0; this._velY = 0;
      this._hasControl = true;
      this._prevT = 0;
    } else {
      const now = performance.now();
      const dt = this._prevT ? Math.min(0.05, (now - this._prevT) / 1000) : 0.016;
      this._prevT = now;
      if (!this._hasControl) {
        this._currentCX = targetCX; this._currentCY = targetCY;
        this._hasControl = true;
      }
      if (tensa) {
        const k = 18; const damp = 5.5;
        const ax = (targetCX - this._currentCX) * k - this._velX * damp;
        const ay = (targetCY - this._currentCY) * k - this._velY * damp;
        this._velX += ax * dt; this._velY += ay * dt;
        this._currentCX += this._velX * dt; this._currentCY += this._velY * dt;
      } else {
        const stiffness = 10 + (1 - swingAmt) * 22;
        const damping = 3.5 + swingAmt * 2;
        const ax = (targetCX - this._currentCX) * stiffness - this._velX * damping;
        const ay = (targetCY - this._currentCY) * stiffness - this._velY * damping;
        this._velX += ax * dt; this._velY += ay * dt;
        this._currentCX += this._velX * dt * 1.19;
        this._currentCY += this._velY * dt * 1.19;
      }
      useCX = this._currentCX; useCY = this._currentCY;
    }

    ctx.save();
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.shadowColor = "rgba(54,218,208,0.22)"; ctx.shadowBlur = 6;
    ctx.strokeStyle = "rgba(54,218,208,0.98)";
    ctx.lineWidth = tensa ? 1.5 : 2.25;
    ctx.beginPath();
    if (tensa) {
      ctx.moveTo(tip.x, tip.y);
      ctx.lineTo(anchor.x, anchor.y);
    } else {
      if (holgura < 1.5) {
        ctx.moveTo(tip.x, tip.y);
        ctx.lineTo(anchor.x, anchor.y);
      } else {
        ctx.moveTo(tip.x, tip.y);
        ctx.quadraticCurveTo(useCX, useCY, anchor.x, anchor.y);
      }
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "rgba(255,255,255,1)"; ctx.beginPath(); ctx.arc(tip.x, tip.y, 2.3, 0, TAU); ctx.fill(); ctx.strokeStyle = "rgba(12,22,24,0.9)"; ctx.lineWidth = 1; ctx.stroke();
    ctx.strokeStyle = "rgba(54,218,208,1)"; ctx.lineWidth = 1.7; ctx.beginPath(); ctx.arc(anchor.x, anchor.y, 6.7, 0, TAU); ctx.stroke(); ctx.fillStyle = "rgba(54,218,208,0.13)"; ctx.fill();
    ctx.fillStyle = "rgba(54,218,208,1)"; ctx.beginPath(); ctx.arc(anchor.x, anchor.y, 1.5, 0, TAU); ctx.fill();
    ctx.restore();
  }

  private drawWalls(
    ctx: CanvasRenderingContext2D,
    walls: { x: number; y: number; w: number; h: number },
    camera: Camera,
  ): void {
    const a = camera.worldToScreen({ x: walls.x, y: walls.y });
    const b = camera.worldToScreen({ x: walls.x + walls.w, y: walls.y });
    const c = camera.worldToScreen({ x: walls.x + walls.w, y: walls.y + walls.h });
    const d = camera.worldToScreen({ x: walls.x, y: walls.y + walls.h });
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.lineTo(c.x, c.y);
    ctx.lineTo(d.x, d.y);
    ctx.closePath();
    ctx.setLineDash([8, 7]);
    ctx.strokeStyle = "rgba(120,132,158,0.55)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  private drawColliders(
    ctx: CanvasRenderingContext2D,
    bodies: readonly Body[],
    camera: Camera,
  ): void {
    ctx.strokeStyle = "rgba(255, 96, 128, 0.85)";
    ctx.lineWidth = 1;
    for (const body of bodies) {
      ctx.beginPath();
      if (!body.local) {
        const p = camera.worldToScreen(body.pos);
        ctx.arc(p.x, p.y, body.shape.size * camera.zoom, 0, TAU);
      } else {
        for (let i = 0; i < body.world.length; i++) {
          const p = camera.worldToScreen(body.world[i]);
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        }
        ctx.closePath();
      }
      ctx.stroke();
    }
  }

  /**
   * Radio de alcance de puentes de cada cuerpo: un circulo discontinuo del
   * tamano hasta donde ese cuerpo tiende puentes. Verde = alcance propio del
   * cuerpo; azul tenue = hereda el valor global. Los cuerpos sin alcance (0) no
   * dibujan nada.
   */
  private drawBridgeReach(
    ctx: CanvasRenderingContext2D,
    bodies: readonly Body[],
    globalReach: number,
    camera: Camera,
  ): void {
    ctx.save();
    ctx.setLineDash([5, 5]);
    ctx.lineWidth = 1.25;
    for (const body of bodies) {
      const own = body.bridgeReach >= 0;
      const reach = own ? body.bridgeReach : globalReach;
      if (reach <= 0) continue;
      const p = camera.worldToScreen(body.pos);
      const r = (body.radius + reach) * camera.zoom;
      ctx.strokeStyle = own ? "rgba(90, 220, 150, 0.9)" : "rgba(79, 140, 255, 0.5)";
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, TAU);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.restore();
  }

  /** Pivote de rotacion/escala: cruz con circulo, siempre en tamano de pantalla. */
  private drawPivot(ctx: CanvasRenderingContext2D, p: Vec2, camera: Camera): void {
    const s = camera.worldToScreen(p);
    ctx.save();
    ctx.strokeStyle = HOT;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(s.x, s.y, 9, 0, TAU);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(s.x - 14, s.y);
    ctx.lineTo(s.x + 14, s.y);
    ctx.moveTo(s.x, s.y - 14);
    ctx.lineTo(s.x, s.y + 14);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(s.x, s.y, 2.5, 0, TAU);
    ctx.fillStyle = HOT;
    ctx.fill();
    ctx.restore();
  }

  private drawSelectBox(ctx: CanvasRenderingContext2D, b: { x: number; y: number; w: number; h: number }, pivot: Vec2 | null, op: import("../app/editor").SelectOp, camera: Camera): void {
    const a = camera.worldToScreen({ x: b.x, y: b.y });
    const c = camera.worldToScreen({ x: b.x + b.w, y: b.y + b.h });
    const w = c.x - a.x, h = c.y - a.y;
    ctx.save();
    ctx.strokeStyle = op === "rotate" ? "rgba(180,220,255,0.95)" : op === "scale" ? "rgba(120,255,180,0.95)" : op === "pivot" ? "rgba(255,195,80,0.95)" : "rgba(100,150,255,0.95)";
    ctx.lineWidth = 1.5;
    ctx.setLineDash(op === "move" ? [6, 4] : []);
    ctx.strokeRect(a.x, a.y, w, h);
    ctx.setLineDash([]);
    // Handles for scale
    if (op === "scale") {
      const hs = 6;
      const pts = [
        [a.x, a.y], [a.x + w / 2, a.y], [a.x + w, a.y],
        [a.x + w, a.y + h / 2], [a.x + w, a.y + h], [a.x + w / 2, a.y + h],
        [a.x, a.y + h], [a.x, a.y + h / 2],
      ] as const;
      ctx.fillStyle = "#fff"; ctx.strokeStyle = "rgba(0,0,0,0.6)";
      for (const [x, y] of pts) { ctx.fillRect(x - hs / 2, y - hs / 2, hs, hs); ctx.strokeRect(x - hs / 2, y - hs / 2, hs, hs); }
    }
    if (op === "rotate") {
      // small rotation handle above top-center
      const rx = a.x + w / 2, ry = a.y - 18;
      ctx.beginPath(); ctx.moveTo(a.x + w / 2, a.y); ctx.lineTo(rx, ry); ctx.stroke();
      ctx.beginPath(); ctx.arc(rx, ry, 6, 0, Math.PI * 2); ctx.fillStyle = "#fff"; ctx.fill(); ctx.stroke();
    }
    if (pivot) {
      const s = camera.worldToScreen(pivot);
      ctx.fillStyle = "rgba(255,195,80,1)"; ctx.strokeStyle = "rgba(0,0,0,0.6)";
      ctx.beginPath(); ctx.arc(s.x, s.y, 5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(s.x - 8, s.y); ctx.lineTo(s.x + 8, s.y); ctx.moveTo(s.x, s.y - 8); ctx.lineTo(s.x, s.y + 8); ctx.stroke();
    }
    ctx.restore();
  }

  private drawHighlight(ctx: CanvasRenderingContext2D, body: Body, camera: Camera): void {    const poly = outlinePolygon(body.shape, 64);
    const cos = Math.cos(body.angle);
    const sin = Math.sin(body.angle);
    ctx.beginPath();
    for (let i = 0; i < poly.length; i++) {
      const lx = poly[i].x;
      const ly = poly[i].y;
      const p = camera.worldToScreen({
        x: body.pos.x + lx * cos - ly * sin,
        y: body.pos.y + lx * sin + ly * cos,
      });
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  private drawShapePreview(
    ctx: CanvasRenderingContext2D,
    shape: ShapeDef,
    cursor: Vec2,
    camera: Camera,
  ): void {
    const poly = outlinePolygon(shape, 72);
    ctx.beginPath();
    for (let i = 0; i < poly.length; i++) {
      const x = cursor.x + poly[i].x * camera.zoom;
      const y = cursor.y + poly[i].y * camera.zoom;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fillStyle = ACCENT_SOFT;
    ctx.fill();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  /**
   * Gizmo de simetria arrastrable.
   *
   * Origen (disco), angulo (tirador sobre el eje) y numero de sectores
   * (tirador perpendicular). Se dibuja en pantalla, no en mundo, para que su
   * tamano no dependa del zoom y siga siendo agarrable siempre.
   */
  private drawSymmetry(ctx: CanvasRenderingContext2D, state: OverlayState, camera: Camera): void {
    const s = state.symmetry;
    const o = camera.worldToScreen({ x: s.x, y: s.y });
    const ang = s.angle + camera.rotation;
    const active = state.activeHandle;
    const hover = state.hoverHandle;
    const colorOf = (h: SymmetryHandle): string =>
      active === h ? HOT : hover === h ? "#ffffff" : ACCENT;

    const diag = Math.hypot(camera.width, camera.height);

    ctx.save();
    ctx.globalAlpha = s.locked ? 0.45 : 1;

    // Ejes de simetria proyectados a lo largo del viewport.
    ctx.setLineDash([6, 6]);
    ctx.lineWidth = 1.25;
    ctx.strokeStyle = "rgba(79,140,255,0.75)";
    const lines = s.mode === "mirror" ? 1 : Math.max(1, Math.round(s.count));
    for (let i = 0; i < lines; i++) {
      const a = ang + (Math.PI * i) / lines;
      ctx.beginPath();
      ctx.moveTo(o.x - Math.cos(a) * diag, o.y - Math.sin(a) * diag);
      ctx.lineTo(o.x + Math.cos(a) * diag, o.y + Math.sin(a) * diag);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // Tirador de angulo.
    const hx = o.x + Math.cos(ang) * GIZMO.axisRadius;
    const hy = o.y + Math.sin(ang) * GIZMO.axisRadius;
    ctx.beginPath();
    ctx.moveTo(o.x, o.y);
    ctx.lineTo(hx, hy);
    ctx.strokeStyle = colorOf("axis");
    ctx.lineWidth = 2;
    ctx.stroke();
    dot(ctx, hx, hy, 7, colorOf("axis"));

    // Tirador de sectores.
    if (s.mode === "radial" || s.mode === "kaleido") {
      const ca = ang + Math.PI / 2;
      const cx = o.x + Math.cos(ca) * GIZMO.countRadius;
      const cy = o.y + Math.sin(ca) * GIZMO.countRadius;
      ctx.beginPath();
      ctx.arc(o.x, o.y, GIZMO.countRadius, ca - 0.5, ca + 0.5);
      ctx.strokeStyle = colorOf("count");
      ctx.lineWidth = 2;
      ctx.stroke();
      dot(ctx, cx, cy, 7, colorOf("count"));
      ctx.font = "600 11px ui-sans-serif, system-ui, sans-serif";
      ctx.fillStyle = "#e8ecf6";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(Math.round(s.count)), cx, cy - 15);
    }

    // Origen.
    ctx.beginPath();
    ctx.arc(o.x, o.y, GIZMO.originRadius, 0, TAU);
    ctx.fillStyle = "rgba(14,18,28,0.55)";
    ctx.fill();
    ctx.strokeStyle = colorOf("origin");
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(o.x, o.y, 2.5, 0, TAU);
    ctx.fillStyle = colorOf("origin");
    ctx.fill();

    ctx.restore();
  }
}

function dot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = "rgba(10,12,18,0.7)";
  ctx.lineWidth = 1.5;
  ctx.stroke();
}
